import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from 'react-router-dom';
import {
  AppBar, Toolbar, Typography, Button, Box,
  BottomNavigation, BottomNavigationAction, Paper, useMediaQuery, Badge,
} from '@mui/material';
import HomeIcon from '@mui/icons-material/Home';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import GroupsIcon from '@mui/icons-material/Groups';
import CelebrationIcon from '@mui/icons-material/Celebration';
import ChatIcon from '@mui/icons-material/Chat';
import StorefrontIcon from '@mui/icons-material/Storefront';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import theme from './theme';
import LoginPage from './components/LoginPage';
import HomePage from './pages/HomePage';
import TreePage from './pages/TreePage';
import FamilyPage from './pages/FamilyPage';
import EventsPage from './pages/EventsPage';
import ChatPage from './pages/ChatPage';
import MarketPage from './pages/MarketPage';
import HistoryPage from './pages/HistoryPage';
import ProfilePage from './pages/ProfilePage';
import AdminPage from './pages/AdminPage';
import NotificationSettings from './pages/NotificationSettings';
import { getSavedUser, getToken, logout, getUnreadTotal } from './api';
import { pushSupported, getNotificationPermission, enablePush } from './push';

const ADMIN_ROLES = ['ADMIN', 'PRESIDENT'];

const NavShell = ({ currentUser }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:600px)');
  const isAdmin = ADMIN_ROLES.includes(currentUser?.role);
  const [unreadTotal, setUnreadTotal] = React.useState(0);

  // Prompt to turn on notifications right after sign-in (WhatsApp-style).
  // If permission was already granted, this quietly re-subscribes this device
  // with the backend; if the member previously denied, we don't nag them —
  // they can still turn it on from the Settings (bell) page.
  React.useEffect(() => {
    if (!pushSupported()) return;
    if (getNotificationPermission() === 'denied') return;
    enablePush().catch(() => {});
  }, []);

  // Poll the unread total so the Chat badge stays fresh from anywhere
  React.useEffect(() => {
    let alive = true;
    const tick = () => getUnreadTotal().then((d) => { if (alive) setUnreadTotal(d.total || 0); }).catch(() => {});
    tick();
    const id = setInterval(tick, 15000);
    return () => { alive = false; clearInterval(id); };
  }, [location.pathname]);

  // Mirror the unread count onto the phone's home-screen app icon — the
  // little red number WhatsApp shows even when the app itself is closed.
  // Supported on Android/Chrome; iOS Safari doesn't support the Badging
  // API for installed PWAs as of writing, so this silently no-ops there.
  React.useEffect(() => {
    if (!('setAppBadge' in navigator)) return;
    if (unreadTotal > 0) {
      navigator.setAppBadge(unreadTotal).catch(() => {});
    } else if ('clearAppBadge' in navigator) {
      navigator.clearAppBadge().catch(() => {});
    }
  }, [unreadTotal]);

  const navItems = [
    { label: 'Home', path: '/', icon: <HomeIcon /> },
    { label: 'Tree', path: '/tree', icon: <AccountTreeIcon /> },
    { label: 'Events', path: '/events', icon: <CelebrationIcon /> },
    { label: 'Chat', path: '/chat', icon: (
      <Badge badgeContent={unreadTotal} color="error" max={99}
        sx={{ '& .MuiBadge-badge': { fontWeight: 700 } }}>
        <ChatIcon />
      </Badge>
    ) },
    { label: 'Market', path: '/market', icon: <StorefrontIcon /> },
    { label: 'Our History', path: '/history', icon: <AutoStoriesIcon /> },
    { label: 'My Family', path: '/family', icon: <GroupsIcon /> },
    { label: 'Settings', path: '/notifications', icon: <NotificationsIcon /> },
    ...(isAdmin ? [{ label: 'Admin', path: '/admin', icon: <AdminPanelSettingsIcon /> }] : []),
  ];

  // On mobile the bottom bar holds the 5 core tabs; My Family and Admin
  // are reached through their cards on the Home page.
  const mobileNavItems = navItems.filter((i) => !['/family', '/admin', '/history', '/notifications'].includes(i.path)).slice(0, 5);
  const barItems = isMobile ? mobileNavItems : navItems;

  const currentTab = barItems.findIndex((i) =>
    i.path === '/' ? location.pathname === '/' : location.pathname.startsWith(i.path)
  );

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <AppBar position="sticky" sx={{ background: 'linear-gradient(135deg, #059669 0%, #065F46 100%)' }}>
        <Toolbar variant="dense">
          <Typography variant="h6" sx={{ flexGrow: 1, fontWeight: 600, fontSize: isMobile ? '1rem' : '1.15rem' }}>
            Mbaa Mwendwa
          </Typography>
          {!isMobile && barItems.map((item) => (
            <Button
              key={item.path}
              color="inherit"
              startIcon={item.icon}
              onClick={() => navigate(item.path)}
              sx={{
                textTransform: 'none', mx: 0.5,
                borderBottom: currentTab === barItems.indexOf(item) ? '2px solid #fff' : '2px solid transparent',
                borderRadius: 0,
              }}
            >
              {item.label}
            </Button>
          ))}
          <Button color="inherit" startIcon={<LogoutIcon />} onClick={logout} sx={{ textTransform: 'none', ml: 1 }}>
            {isMobile ? '' : `Logout${currentUser?.name ? ` (${currentUser.name.split(' ')[0]})` : ''}`}
          </Button>
        </Toolbar>
      </AppBar>

      <Box sx={{ flex: 1, pb: isMobile ? 7 : 0 }}>
        <Routes>
          <Route path="/" element={<HomePage currentUser={currentUser} />} />
          <Route path="/tree" element={<TreePage currentUser={currentUser} />} />
          <Route path="/events" element={<EventsPage currentUser={currentUser} />} />
          <Route path="/chat" element={<ChatPage currentUser={currentUser} />} />
          <Route path="/market" element={<MarketPage currentUser={currentUser} />} />
          <Route path="/history" element={<HistoryPage />} />
          <Route path="/person/:id" element={<ProfilePage currentUser={currentUser} />} />
          <Route path="/family" element={<FamilyPage currentUser={currentUser} />} />
          <Route path="/notifications" element={<NotificationSettings />} />
          {isAdmin && <Route path="/admin" element={<AdminPage currentUser={currentUser} />} />}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Box>

      {isMobile && (
        <Paper sx={{ position: 'fixed', bottom: 0, left: 0, right: 0, zIndex: 1300 }} elevation={8}>
          <BottomNavigation
            showLabels
            value={currentTab === -1 ? 0 : currentTab}
            onChange={(e, newValue) => navigate(barItems[newValue].path)}
          >
            {barItems.map((item) => (
              <BottomNavigationAction key={item.path} label={item.label} icon={item.icon} />
            ))}
          </BottomNavigation>
        </Paper>
      )}
    </Box>
  );
};

const App = () => {
  const [currentUser, setCurrentUser] = useState(getSavedUser());
  const isAuthenticated = Boolean(currentUser && getToken());

  // Keep the saved user in sync if another tab logs out
  useEffect(() => {
    const onStorage = () => {
      if (!getToken()) setCurrentUser(null);
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {!isAuthenticated ? (
        <LoginPage onLogin={setCurrentUser} />
      ) : (
        <BrowserRouter>
          <NavShell currentUser={currentUser} />
        </BrowserRouter>
      )}
    </ThemeProvider>
  );
};

export default App;
